import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

const backgroundImages = [
  "/renamed_website_images/smart-classes-mobile.jpg",
  "/renamed_website_images/smart-classroom.jpg",
  "/renamed_website_images/smart-classroom-session-2.jpg",
];

const features = [
  {
    tag: "Interactive Boards",
    title: "Smart Board Integration",
    desc: "Touch-enabled boards and multimedia tools enable interactive learning in every classroom.",
    color: "#3b82f6",
    border: "rgba(59,130,246,0.2)",
  },
  {
    tag: "Digital Content",
    title: "Online Resources",
    desc: "Curated digital content, quizzes, and learning videos enrich student understanding.",
    color: "#f4c842",
    border: "rgba(244,200,66,0.2)",
  },
  {
    tag: "Engaging Tools",
    title: "Collaborative Learning",
    desc: "Live annotations, presentations, and discussion tools foster student collaboration.",
    color: "#8b5cf6",
    border: "rgba(139,92,246,0.2)",
  },
];

const SmartClasses = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length,
      );
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden bg-[#020810]">
      {/* Smooth Cross-fade Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {backgroundImages.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url('${img}')`,
              opacity: currentImageIndex === idx ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
            }}
          />
        ))}
      </div>

      {/* Dark overlay */}
      <div
        className="absolute inset-0 z-1"
        style={{ background: "rgba(2,8,16,0.88)" }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <Link
          to="/facilities"
          className="inline-block text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Smart Classes
        </h1>
        <div className="cosmic-divider mb-10" />

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {features.map((f, i) => (
            <div
              key={i}
              className="glass-card p-7 transition-all duration-300 hover:scale-[1.02]"
              style={{ borderColor: f.border }}
            >
              <span
                className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-4"
                style={{
                  fontFamily: "Nunito, sans-serif",
                  background: `${f.color}18`,
                  border: `1px solid ${f.border}`,
                  color: f.color,
                }}
              >
                {f.tag}
              </span>
              <h3
                className="font-bold mb-3"
                style={{
                  fontFamily: "Cinzel, serif",
                  color: "#f0f5ff",
                  fontSize: "1rem",
                }}
              >
                {f.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ fontFamily: "Nunito, sans-serif", color: "#8899b8" }}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {[
            "/renamed_website_images/smart-classroom-session-2.jpg",
            "/renamed_website_images/smart-classroom-session-3.jpg",
          ].map((src, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden"
              style={{
                border: "1px solid rgba(139,92,246,0.2)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}
            >
              <img
                src={src}
                alt={`Smart Class ${i + 1}`}
                className="w-full object-cover"
                style={{ height: "220px" }}
              />
            </div>
          ))}
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-white/10">
          <Link
            to="/facilities/computer-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: Computer Lab
          </Link>
          <Link
            to="/facilities/library"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: School Library <FaArrowRight />
          </Link>
        </div>
      </div>

      {/* Side floating arrows */}
      <Link
        to="/facilities/computer-lab"
        aria-label="Previous: Computer Lab"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/library"
        aria-label="Next: School Library"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default SmartClasses;
